import { useState, useCallback } from "react";
import { searchGraphUsers, GraphUser } from "../../../../../../utils/MsGraphApiCall";

interface UseUserSearchResult {
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    searchResults: GraphUser[];
    isSearching: boolean;
    searchError: string | null;
    clearSearchError: () => void;
    handleSearch: () => Promise<void>;
    resetSearch: () => void;
}

export const useUserSearch = (): UseUserSearchResult => {
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState<GraphUser[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);

    const clearSearchError = useCallback(() => {
        setSearchError(null);
    }, []);

    const handleSearch = useCallback(async () => {
        if (!searchTerm.trim() || searchTerm.length < 2) {
            setSearchError("Please enter at least 2 characters to search");
            return;
        }

        setIsSearching(true);
        setSearchError(null);

        try {
            const users = await searchGraphUsers(searchTerm);
            setSearchResults(users);
            if (users.length === 0) {
                setSearchError("No users found matching your search");
            }
        } catch (err) {
            console.error("Error searching users:", err);
            setSearchError("Failed to search users. Please check your permissions and try again.");
        } finally {
            setIsSearching(false);
        }
    }, [searchTerm]);

    const resetSearch = useCallback(() => {
        setSearchTerm("");
        setSearchResults([]);
        setSearchError(null);
    }, []);

    return {
        searchTerm,
        setSearchTerm,
        searchResults,
        isSearching,
        searchError,
        clearSearchError,
        handleSearch,
        resetSearch
    };
};
